import { isMobile } from '../../utils/mediaQueries'

// ============================================================================
// Constants
// ============================================================================

const CUSTOMER_STORY_TEXT_CLASSES = {
  textContainer: 'customerstorytextcontainer',
  text: 'cmp-text',
}

const GLOSSARY_CARD_CLASSES = {
  infoIcon: 'customerstorytext__glossary-card--info-icon',
  modal: 'customerstorytext__glossary-card-modal',
  modalOverlay: 'customerstorytext__glossary-card-modal--overlay',
  modalHidden: 'customerstorytext__glossary-card-modal--hidden',
  modalCloseButton: 'customerstorytext__glossary-card-modal--close-button',
  modalCloseButtonIcon: 'customerstorytext__glossary-card-modal--close-button-icon',
  modalTitle: 'customerstorytext__glossary-card-modal--title',
  modalDescription: 'customerstorytext__glossary-card-modal--description',
  modalLink: 'customerstorytext__glossary-card-modal--link',
}

const ARIA_ATTRIBUTES = {
  expanded: 'aria-expanded',
  pressed: 'aria-pressed',
  label: 'aria-label',
  modal: 'aria-modal',
  labelledBy: 'aria-labelledby',
  describedBy: 'aria-describedby',
  hidden: 'aria-hidden',
}

// ============================================================================
// Data Extraction
// ============================================================================

function extractGlossaryCardData(data) {
  const glossaryCardsData = []

  for (const item of data.glossaryCards || []) {
    const { closeButtonAriaLabel, description, infoIconAriaLabel, linkLabel, link, title } = item || {}

    glossaryCardsData.push({
      title: title || '',
      closeButtonAriaLabel: closeButtonAriaLabel || '',
      description: description || '',
      infoIconAriaLabel: infoIconAriaLabel || '',
      linkLabel: linkLabel || '',
      href: link?.externalizedURL || '',
    })
  }

  return glossaryCardsData
}

function findGlossaryCardByHref(href, glossaryCardsData) {
  return glossaryCardsData.find((card) => card.href === href)
}

// ============================================================================
// Modal Element Creation
// ============================================================================

function createCloseButton(glossaryCardData) {
  const closeButton = document.createElement('button')
  closeButton.classList.add(GLOSSARY_CARD_CLASSES.modalCloseButton)
  closeButton.setAttribute(ARIA_ATTRIBUTES.expanded, 'false')
  closeButton.setAttribute(ARIA_ATTRIBUTES.label, glossaryCardData.closeButtonAriaLabel)
  closeButton.setAttribute('type', 'button')

  const closeIcon = document.createElement('i')
  closeIcon.classList.add('ri-close-line', GLOSSARY_CARD_CLASSES.modalCloseButtonIcon)
  closeIcon.setAttribute(ARIA_ATTRIBUTES.hidden, 'true')
  closeButton.append(closeIcon)

  return closeButton
}

function createModalTitle(glossaryCardData, titleId) {
  const title = document.createElement('div')
  title.id = titleId
  title.classList.add(GLOSSARY_CARD_CLASSES.modalTitle)
  title.textContent = glossaryCardData.title
  return title
}

function createModalDescription(glossaryCardData, descriptionId) {
  const description = document.createElement('p')
  description.id = descriptionId
  description.classList.add(GLOSSARY_CARD_CLASSES.modalDescription)
  description.textContent = glossaryCardData.description
  return description
}

function createModalLink(glossaryCardData) {
  if (!glossaryCardData.linkLabel || !glossaryCardData.href) {
    return null
  }

  const link = document.createElement('a')
  link.textContent = glossaryCardData.linkLabel
  link.href = glossaryCardData.href
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  link.classList.add(GLOSSARY_CARD_CLASSES.modalLink)
  link.setAttribute(ARIA_ATTRIBUTES.label, `${glossaryCardData.linkLabel} ${glossaryCardData.title}`)

  link.addEventListener('click', () => {
    trackCtaClick(glossaryCardData)
  })

  return link
}

function createModalOverlay() {
  const modalOverlay = document.createElement('div')
  modalOverlay.classList.add(GLOSSARY_CARD_CLASSES.modalOverlay)
  modalOverlay.classList.add(GLOSSARY_CARD_CLASSES.modalHidden)
  modalOverlay.setAttribute('role', 'presentation')
  return modalOverlay
}

function createModal(titleId, descriptionId) {
  const modal = document.createElement('div')
  modal.classList.add(GLOSSARY_CARD_CLASSES.modal)
  modal.setAttribute('role', 'dialog')
  modal.setAttribute(ARIA_ATTRIBUTES.modal, 'true')
  modal.setAttribute(ARIA_ATTRIBUTES.labelledBy, titleId)
  modal.setAttribute(ARIA_ATTRIBUTES.describedBy, descriptionId)
  return modal
}

function generateUniqueId(prefix) {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`
}

// ============================================================================
// Modal Handlers Setup
// ============================================================================

function setupCloseButtonHandlers(closeButton, modalOverlay) {
  const handleClose = () => {
    modalOverlay.classList.add(GLOSSARY_CARD_CLASSES.modalHidden)
    modalOverlay.setAttribute(ARIA_ATTRIBUTES.hidden, 'true')
  }

  closeButton.addEventListener('click', (event) => {
    event.stopPropagation()
    handleClose()
  })

  closeButton.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      handleClose()
    }
  })
}

function createGlossaryCardModal(glossaryCardData) {
  const titleId = generateUniqueId('glossary-card-title')
  const descriptionId = generateUniqueId('glossary-card-description')

  const modalOverlay = createModalOverlay()
  const modal = createModal(titleId, descriptionId)

  // Add close button
  const closeButton = createCloseButton(glossaryCardData)
  setupCloseButtonHandlers(closeButton, modalOverlay)
  modal.append(closeButton)

  // Add title
  const title = createModalTitle(glossaryCardData, titleId)
  modal.append(title)

  // Add description
  const description = createModalDescription(glossaryCardData, descriptionId)
  modal.append(description)

  // Add link if available
  const link = createModalLink(glossaryCardData)
  if (link) {
    modal.append(link)
  }

  modalOverlay.append(modal)
  return modalOverlay
}

// ============================================================================
// Analytics Tracking
// ============================================================================

function trackIconClick(glossaryCardData) {
  window.adobeDataLayer?.push({
    event: 'glossary card icon click',
    eventInfo: {
      reference: 'glossary card',
      title: glossaryCardData.title,
    },
  })
}

function trackCtaClick(glossaryCardData) {
  window.adobeDataLayer?.push({
    event: 'glossary card cta click',
    eventInfo: {
      reference: 'glossary card',
      title: glossaryCardData.title,
      link: glossaryCardData.href,
      label: glossaryCardData.linkLabel,
    },
  })
}

// ============================================================================
// Info Icon Creation
// ============================================================================

function createGlossaryInfoIcon(glossaryCardData) {
  const supElement = document.createElement('sup')
  supElement.textContent = 'i'
  supElement.classList.add(GLOSSARY_CARD_CLASSES.infoIcon)
  supElement.setAttribute('tabindex', '0')
  supElement.setAttribute('role', 'button')
  supElement.setAttribute(ARIA_ATTRIBUTES.label, `${glossaryCardData.infoIconAriaLabel} ${glossaryCardData.title}`)
  supElement.setAttribute(ARIA_ATTRIBUTES.pressed, 'false')
  supElement.setAttribute('type', 'button')
  return supElement
}

// ============================================================================
// Modal Visibility & Event Handlers
// ============================================================================

function closeOtherModals(modal, containerElement) {
  const allModals = containerElement.querySelectorAll(`.${GLOSSARY_CARD_CLASSES.modalOverlay}`)
  for (const otherModal of allModals) {
    if (otherModal !== modal) {
      otherModal.classList.add(GLOSSARY_CARD_CLASSES.modalHidden)
      otherModal.setAttribute(ARIA_ATTRIBUTES.hidden, 'true')
    }
  }
}

function scrollElementToCenter(element) {
  const rect = element.getBoundingClientRect()
  const elementCenter = rect.top + window.scrollY + rect.height / 2
  const targetTop = elementCenter - window.innerHeight / 2
  window.scrollTo({ top: Math.max(targetTop, 0), behavior: 'smooth' })
}

function closeModal(modal, supElement) {
  modal.classList.add(GLOSSARY_CARD_CLASSES.modalHidden)
  modal.setAttribute(ARIA_ATTRIBUTES.hidden, 'true')
  supElement.setAttribute(ARIA_ATTRIBUTES.pressed, 'false')
}

function handleModalToggle({ supElement, modal, textElement, containerElement, glossaryCardData }) {
  if (containerElement) {
    closeOtherModals(modal, containerElement)
  }

  const isHidden = modal.classList.toggle(GLOSSARY_CARD_CLASSES.modalHidden)
  const isNowVisible = !isHidden

  supElement.setAttribute(ARIA_ATTRIBUTES.pressed, isNowVisible ? 'true' : 'false')

  if (isNowVisible) {
    trackIconClick(glossaryCardData)

    // Scroll so the clicked text component is centered in the viewport
    // (works on desktop, tablet and mobile)
    if (textElement) {
      scrollElementToCenter(textElement)
    }
  }
}

function setupGlossaryCardClickHandler({ supElement, modal, textElement, containerElement, glossaryCardData }) {
  // Handle click event
  supElement.addEventListener('click', (event) => {
    event.stopPropagation()
    handleModalToggle({ supElement, modal, textElement, containerElement, glossaryCardData })
  })

  // Handle keyboard events
  supElement.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      event.stopPropagation()
      handleModalToggle({ supElement, modal, textElement, containerElement, glossaryCardData })
    } else if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      closeModal(modal, supElement)
    }
  })

  setupClickOutsideToClose({ supElement, modal })
}

// Close the modal when tapping the backdrop (anywhere outside the card) on
// mobile. On mobile the overlay is a full-window backdrop, so we test against
// the card element rather than the overlay. The trigger icon and close button
// stop propagation, so they don't reach this document-level listener.
function setupClickOutsideToClose({ supElement, modal }) {
  const card = modal.querySelector(`.${GLOSSARY_CARD_CLASSES.modal}`)

  document.addEventListener('click', (event) => {
    if (!isMobile()) {
      return
    }

    if (modal.classList.contains(GLOSSARY_CARD_CLASSES.modalHidden)) {
      return
    }

    if (supElement.contains(event.target) || (card && card.contains(event.target))) {
      return
    }

    closeModal(modal, supElement)
  })
}

// ============================================================================
// Main Initialization
// ============================================================================

function processGlossaryLink(linkElement, glossaryCardData, textElement, containerElement) {
  const supElement = createGlossaryInfoIcon(glossaryCardData)
  const modal = createGlossaryCardModal(glossaryCardData)

  // Append the modal to its own text component so it is positioned absolute
  // top-left of that component. When multiple text components share a
  // container, each modal sits on top of the component that owns it.
  textElement.append(modal)
  setupGlossaryCardClickHandler({ supElement, modal, textElement, containerElement, glossaryCardData })

  // Get the link text
  const linkText = linkElement.textContent

  // Create a text node with the link text
  const textNode = document.createTextNode(linkText)

  // Replace the link with text and icon
  linkElement.replaceWith(textNode, supElement)
}

function initCustomerStoryText(customerStoryTextElement, data) {
  const allLinksElements = customerStoryTextElement.querySelectorAll('a')
  const containerElement = customerStoryTextElement.closest(`.${CUSTOMER_STORY_TEXT_CLASSES.textContainer}`)
  const glossaryCardsData = extractGlossaryCardData(data)

  for (const linkElement of allLinksElements) {
    const href = linkElement.getAttribute('href')
    const glossaryCardData = findGlossaryCardByHref(href, glossaryCardsData)

    if (!glossaryCardData) {
      continue
    }

    processGlossaryLink(linkElement, glossaryCardData, customerStoryTextElement, containerElement)
  }
}

function initializeCustomerStoryTextElements() {
  const customerStoryTextElements = document.querySelectorAll(
    `.${CUSTOMER_STORY_TEXT_CLASSES.textContainer} .${CUSTOMER_STORY_TEXT_CLASSES.text}`,
  )

  for (const customerStoryTextElement of customerStoryTextElements) {
    const dataJson = customerStoryTextElement.dataset?.json
    if (dataJson) {
      try {
        const data = JSON.parse(dataJson)
        initCustomerStoryText(customerStoryTextElement, data)
      } catch (error) {
        console.error('Error parsing JSON for customer story text:', error)
      }
    }
  }
}

// ============================================================================
// DOM Ready Handler
// ============================================================================

;(function () {
  function onDocumentReady() {
    initializeCustomerStoryTextElements()
  }

  if (document.readyState !== 'loading') {
    onDocumentReady()
  } else {
    document.addEventListener('DOMContentLoaded', onDocumentReady)
  }
})()
